// Themes — the top-level grouping above decks. A theme dictates the
// shape of the cards inside it (bilingual = language pair; mono = two
// faces in the user's UI locale) and carries the subject-area label
// shown in the picker + the onboarding flow.
//
// Strong-theme model (per product decision): decks inherit their
// structural attributes from their theme. A deck's `themeId` is
// required; its runtime shape is looked up on this registry rather
// than duplicated on the deck itself.
//
// The catalog is a const for now; post-Supabase it can become a DB
// query (user-subscribable themes + custom themes).

export type ThemeShape = "bilingual" | "mono";

export type Theme = {
  /** Stable key used in config, localStorage, and (future) DB rows. */
  id: string;
  /** i18n key for the display name ("Langues", "Géographie", …). */
  nameKey: string;
  /** Monochrome glyph for the picker and the onboarding step. Picked
   *  from the existing header icon palette so nothing clashes. */
  icon: string;
  /** All decks under this theme share this shape. A bilingual theme
   *  activates the language-pair / CEFR UI; a mono theme hides them. */
  shape: ThemeShape;
};

export const THEMES: readonly Theme[] = [
  {
    id: "languages",
    nameKey: "theme_languages",
    icon: "♪",
    shape: "bilingual",
  },
  {
    id: "geography",
    nameKey: "theme_geography",
    icon: "◉",
    shape: "mono",
  },
];

export const DEFAULT_THEME_ID = "languages";

/** Resolve a theme by id, falling back to the default when unknown
 *  (stale localStorage pointer from a theme that was renamed, etc.). */
export function getTheme(id: string): Theme {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!;
}
